import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  announcementDialogKey,
  AnnouncementDialogState,
} from './announcement-dialog-reducer';
import * as moment from 'moment';
import { IAnnouncementPopup } from '@app/shared/models/announcement-popups.model';
import { selectUser } from '@app/shared/store/auth/auth.selector';
import { AccessLevelEnum } from '@app/shared/models/user/access-level-enum';

const ANNOUNCEMENT_VIEWS_KEY = 'announcement_views';
const ANNOUNCEMENT_DONT_SHOW_KEY = 'announcement_dont_show';
const ANNOUNCEMENT_SESSION_KEY = 'announcement_session';

const getAnnouncementViews = (announcementId: string): number => {
  const views = localStorage.getItem(ANNOUNCEMENT_VIEWS_KEY);
  if (!views) return 0;

  const viewsMap = JSON.parse(views);
  return viewsMap[announcementId] || 0;
};

const incrementAnnouncementViews = (announcementId: string): void => {
  const views = localStorage.getItem(ANNOUNCEMENT_VIEWS_KEY);
  const viewsMap = views ? JSON.parse(views) : {};

  viewsMap[announcementId] = (viewsMap[announcementId] || 0) + 1;
  localStorage.setItem(ANNOUNCEMENT_VIEWS_KEY, JSON.stringify(viewsMap));
};

const getDontShowAgain = (announcementId: string): boolean => {
  const dontShow = localStorage.getItem(ANNOUNCEMENT_DONT_SHOW_KEY);
  if (!dontShow) return false;

  const dontShowMap = JSON.parse(dontShow);
  return dontShowMap[announcementId] || false;
};

export const selectAnnouncementState =
  createFeatureSelector<AnnouncementDialogState>(announcementDialogKey);

export const selectAnnouncement = createSelector(
  selectAnnouncementState,
  (it: AnnouncementDialogState) => it.announcement
);

export const selectAnnouncementsLoaded = createSelector(
  selectAnnouncementState,
  (it: AnnouncementDialogState) => it.announcementLoaded
);

export const selectValidAnnouncement = createSelector(
  selectAnnouncement,
  selectUser,
  (announcement, meUser) => {
    if (!announcement || !announcement.active) return null;

    const now = moment();
    const startDate = moment(announcement.start_date);
    const endDate = moment(announcement.end_date);

    const isWithinDateRange = now.isBetween(startDate, endDate, 'day', '[]');
    if (!isWithinDateRange) return null;

    const viewCount = getAnnouncementViews(announcement.id);
    const maxViews = 3;

    if (viewCount >= maxViews) return null;

    // Check if announcement was shown in current session storage
    const sessionShown = sessionStorage.getItem(
      `${ANNOUNCEMENT_SESSION_KEY}_${announcement.id}`
    );
    if (sessionShown) return null;
    //

    const userRoles: AccessLevelEnum[] = meUser.userAccesses.map(
      (access) => access.accessLevel
    );

    if (!isRoleCompatible(announcement, userRoles)) return null;

    const dontShow = getDontShowAgain(announcement.id);
    if (dontShow) return null;

    // Mark as shown in current session storage
    sessionStorage.setItem(
      `${ANNOUNCEMENT_SESSION_KEY}_${announcement.id}`,
      'true'
    );
    //
    incrementAnnouncementViews(announcement.id);
    return announcement;
  }
);

const isRoleCompatible = (
  announcement: IAnnouncementPopup,
  userRoles: AccessLevelEnum[]
): boolean => {
  const announcementRoles: AccessLevelEnum[] = announcement.user_roles.map(
    (accessLevel) => accessLevel.level
  ) as AccessLevelEnum[];

  if (announcementRoles.length === 0) return true;
  return announcementRoles.some((role) => userRoles.includes(role));
};
